/**
 * The site's OWN monitor feed — `GET /api/incidents`, a Netlify function on
 * THIS site (see `netlify/functions/`). It is the piece the page always
 * needed: the incident history from the API (`/api/status/history`) dies with
 * the API, so during an outage the page lost its data source exactly when it
 * mattered. This feed is written by the site's scheduled monitor into
 * Netlify Blobs and stays readable while the API is dark.
 *
 * The page renders it as a fallback when the API's history is unreachable,
 * and as confirmation next to a failed live probe.
 */

/** One recorded incident window, written by the scheduled monitor. */
export interface WatchdogIncident {
  /** ISO timestamp the monitor first saw the failure (2 consecutive probes). */
  openedAt: string
  /** ISO timestamp the probe went green again, or `null` while ongoing. */
  closedAt: string | null
  /** Consecutive failures at the moment the incident was opened. */
  failures: number
  /** The last probe error text. */
  lastError: string
  /** Auto-start attempts made when the incident opened. */
  heals?: Array<{
    at: string
    error?: string
    results?: Array<{
      machine?: string
      was?: string
      ok?: boolean
      status?: number
      note?: string
    }>
  }>
  /** Set when the control probe failed too (a monitor-egress problem, no heal). */
  note?: string
}

/**
 * The monitor's identity, read from WHICHEVER feed shape answered. Both live
 * monitors (`mol-watchdog` on Fly and the Cloudflare Worker) put the watched
 * app in `watched` — a bare name on `/incidents` (`"molecule-api"`), an
 * `{ url, app }` object on `/status`. An earlier version of this module
 * assumed a `monitored` object that no monitor has ever emitted.
 */
export interface WatchdogMonitored {
  /** The monitored URL, when the feed names one. */
  url?: string
  /** The monitored app name, when the feed names one. */
  app?: string
}

/**
 * The normalized `/api/incidents` payload. `normalizeWatchdogFeed` guarantees
 * every field the page reads: `monitored` is always an object and `incidents`
 * is always an array, whatever the monitor (or the Netlify proxy's
 * `unavailable()` body) actually answered.
 */
export interface WatchdogFeed {
  /** Who the monitor watches. Fields are absent when the feed did not say. */
  monitored: WatchdogMonitored
  /** The monitor's current probe state (`ok` / `failing` / `down` / `boot`). */
  probe?: string
  lastOkAt?: string | null
  lastError?: string | null
  /** Recorded outage windows; `[]` when the feed carried none. */
  incidents: WatchdogIncident[]
  /** Set when the proxy could read no monitor at all. */
  error?: string
}

/** A plain object we can read named fields from. */
type JsonRecord = Record<string, unknown>

/** Narrow an unknown value to a plain object, or null. */
function asRecord(value: unknown): JsonRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as JsonRecord)
    : null
}

/** A non-empty string from a field, or undefined. */
function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/** One feed incident is usable when it names when it opened (the page renders that). */
function isIncident(value: unknown): value is WatchdogIncident {
  const record = asRecord(value)
  return record !== null && typeof record.openedAt === 'string'
}

/**
 * Read the monitor's identity from `monitored` (`{ url, app }`) or `watched`
 * (an app name, or an `{ url, app }` object). Returns `{}` when the feed
 * names neither — never a crash.
 */
function readMonitored(record: JsonRecord): WatchdogMonitored {
  const monitored = asRecord(record.monitored)
  const watched = record.watched
  const watchedRecord = asRecord(watched)
  const app = asString(monitored?.app) ?? asString(watchedRecord?.app) ?? asString(watched)
  const url = asString(monitored?.url) ?? asString(watchedRecord?.url)
  return { ...(app ? { app } : {}), ...(url ? { url } : {}) }
}

/**
 * Turn whatever the feed answered into the shape the page reads.
 *
 * This is the page's guard against the monitor's actual contract: the feed is
 * proxied verbatim, so an upstream error body, a monitor restart mid-write, or
 * a new monitor that names the watched app differently must NOT be able to
 * throw during render — a render error unmounts the page into a blank screen,
 * which is the one failure a status page may never have.
 *
 * @param raw - The parsed JSON body (any shape).
 * @returns The normalized feed, or null when the body is not an object.
 */
export function normalizeWatchdogFeed(raw: unknown): WatchdogFeed | null {
  const record = asRecord(raw)
  if (!record) return null
  const error = asString(record.error)
  return {
    monitored: readMonitored(record),
    ...(asString(record.probe) ? { probe: asString(record.probe) } : {}),
    ...(asString(record.lastOkAt) ? { lastOkAt: asString(record.lastOkAt) } : {}),
    ...(asString(record.lastError) ? { lastError: asString(record.lastError) } : {}),
    incidents: Array.isArray(record.incidents) ? record.incidents.filter(isIncident) : [],
    ...(error ? { error } : {}),
  }
}

/**
 * Fetch the monitor feed from this site. Returns `null` on any failure — the
 * feed is supplementary and must never break the page.
 *
 * @param timeoutMs - Fetch timeout (the page's other fetches use 5s).
 * @returns The normalized feed, or null.
 */
export async function fetchWatchdogFeed(timeoutMs: number = 5_000): Promise<WatchdogFeed | null> {
  try {
    const res = await fetch('/api/incidents', { signal: AbortSignal.timeout(timeoutMs) })
    if (!res.ok) return null
    return normalizeWatchdogFeed(await res.json())
  } catch (_error) {
    // Intentional noop: the feed is supplementary — any failure (offline,
    // timeout, non-JSON) just means the page renders without it.
    return null
  }
}
